import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, CheckCircle2, Clock, RefreshCw, Save, ExternalLink } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { API_URL } from "../../lib/utils";

const platformHost = typeof window !== "undefined" ? window.location.hostname : "";

export const CustomDomainCard = ({ settings, setSettings, onSave, saving }) => {
  const [checking, setChecking] = useState(false);
  const [check, setCheck] = useState(null);
  const domain = settings.custom_domain || "";
  const verified = settings.custom_domain_verified_at;

  const runCheck = async () => {
    setChecking(true);
    try {
      const r = await axios.post(`${API_URL}/settings/domain/check`, {}, { withCredentials: true });
      setCheck(r.data);
      toast[r.data.dns_ok ? "success" : "info"](r.data.dns_ok ? "DNS correcto — o domínio aponta para a plataforma" : "O DNS ainda não aponta para a plataforma");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível verificar o domínio");
    } finally {
      setChecking(false);
    }
  };

  return (
    <Card data-testid="custom-domain-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5" /> Domínio Próprio (marca branca)</CardTitle>
        <CardDescription>Os seus funcionários entram pelo seu endereço e vêem o seu logótipo e nome no ecrã de login. O logótipo já aparece nas facturas e recibos.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="custom-domain">Domínio</Label>
            <Input id="custom-domain" placeholder="app.minhaempresa.ao" value={domain} onChange={(e) => setSettings({ ...settings, custom_domain: e.target.value })} data-testid="custom-domain-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="brand-color">Cor da marca</Label>
            <div className="flex gap-2">
              <input type="color" value={settings.brand_color || "#22d3ee"} onChange={(e) => setSettings({ ...settings, brand_color: e.target.value })} className="h-10 w-12 rounded-md border bg-transparent p-1" aria-label="Cor da marca" data-testid="brand-color-picker" />
              <Input id="brand-color" placeholder="#22d3ee" value={settings.brand_color || ""} onChange={(e) => setSettings({ ...settings, brand_color: e.target.value })} data-testid="brand-color-input" />
            </div>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="brand-tagline">Frase no ecrã de login (opcional)</Label>
          <Input id="brand-tagline" maxLength={120} placeholder="Ex.: Bem-vindo à equipa. Inicie sessão para continuar." value={settings.brand_tagline || ""} onChange={(e) => setSettings({ ...settings, brand_tagline: e.target.value })} data-testid="brand-tagline-input" />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {domain && (verified
            ? <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 gap-1" data-testid="custom-domain-status"><CheckCircle2 className="w-3.5 h-3.5" /> Verificado em {new Date(verified).toLocaleDateString("pt-AO")}</Badge>
            : <Badge variant="outline" className="gap-1 text-amber-600 border-amber-500/40" data-testid="custom-domain-status"><Clock className="w-3.5 h-3.5" /> Pendente — abra {domain} para verificar</Badge>)}
          <Button size="sm" variant="outline" onClick={onSave} disabled={saving} className="gap-1.5" data-testid="custom-domain-save-btn"><Save className="w-4 h-4" /> Guardar</Button>
          <Button size="sm" variant="outline" onClick={runCheck} disabled={checking || !domain} className="gap-1.5" data-testid="custom-domain-check-btn"><RefreshCw className={`w-4 h-4 ${checking ? "animate-spin" : ""}`} /> Verificar DNS</Button>
          {domain && <a href={`https://${domain}/login`} target="_blank" rel="noreferrer" className="text-sm text-primary inline-flex items-center gap-1 hover:underline" data-testid="custom-domain-open-link"><ExternalLink className="w-3.5 h-3.5" /> Abrir ecrã de login</a>}
        </div>

        {check && (
          <div className="rounded-lg border bg-muted/40 p-3 text-xs space-y-1" data-testid="custom-domain-check-result">
            <p><span className="font-semibold">{check.domain}</span> resolve para: {check.resolves_to.length ? check.resolves_to.join(", ") : "— (sem registo DNS)"}</p>
            <p>Plataforma ({check.platform_host}): {check.platform_ips.join(", ") || "—"}</p>
            <p className={check.dns_ok ? "text-emerald-600" : "text-amber-600"}>{check.dns_ok ? "DNS correcto." : "Crie o registo CNAME indicado abaixo e aguarde a propagação (até 24h)."}</p>
          </div>
        )}

        <ol className="list-decimal pl-5 text-sm text-muted-foreground space-y-1" data-testid="custom-domain-instructions">
          <li>No painel do seu fornecedor de DNS crie um registo <code className="rounded bg-muted px-1">CNAME</code> de <strong>{domain || "app.minhaempresa.ao"}</strong> para <strong>{platformHost}</strong>.</li>
          <li>Peça à LUMORA para activar o domínio na plataforma (certificado HTTPS automático).</li>
          <li>Abra <strong>https://{domain || "app.minhaempresa.ao"}/login</strong> — o estado passa a «Verificado» e o ecrã mostra a sua marca.</li>
        </ol>
      </CardContent>
    </Card>
  );
};
